/**
 * WebSocket-подключение к /api/ws при открытии приложения.
 * Рассылает события (new_message и т.д.) подписчикам.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getInitData } from "../utils/initData.js";

function getWsUrl() {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  const initData = getInitData();
  const qs = initData ? `?init_data=${encodeURIComponent(initData)}` : "";
  return `${protocol}//${host}/api/ws${qs}`;
}

const WebSocketContext = createContext({
  subscribe: () => () => {},
  connectionStatus: "disconnected",
});

export function WebSocketProvider({ children }) {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const notify = useCallback((payload) => {
    try {
      listenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          console.warn("WS listener error", e);
        }
      });
    } catch (e) {
      console.warn("WS notify error", e);
    }
  }, []);

  useEffect(() => {
    const initData = getInitData();
    if (!initData) return;

    let closed = false;

    function connect() {
      if (closed) return;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
        wsRef.current = null;
      }
      const url = getWsUrl();
      setConnectionStatus("connecting");
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (closed) return;
        setConnectionStatus("connected");
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (closed) return;
        try {
          const payload = JSON.parse(event.data);
          notify(payload);
        } catch (e) {
          console.warn("WS parse error", e);
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (closed) return;
        setConnectionStatus("disconnected");
        const delay = Math.min(3000 * 2 ** reconnectAttemptsRef.current, 30000);
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
      };

      ws.onerror = () => {};
    }

    connect();

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // После возврата в WebApp часто “тихо” рвётся сокет — переподключаемся сразу.
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };
    const onOnline = () => {
      reconnectAttemptsRef.current = 0;
      connect();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      closed = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnectionStatus("disconnected");
    };
  }, [notify]);

  const subscribe = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  const value = { subscribe, connectionStatus };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
